import { createHmac, randomBytes, timingSafeEqual } from 'crypto';
import { SERVER_TYPES } from '../constants.js';

/**
 * The roles a dynamic member can be admitted as. A join token names exactly one;
 * the registrant's server type must match it.
 */
export type JoinRole = 'host' | 'connector' | 'area' | 'gate';

export const JOIN_ROLE_SERVER_TYPE: Record<JoinRole, SERVER_TYPES> = {
  host: SERVER_TYPES.HOST,
  connector: SERVER_TYPES.CONNECTOR,
  area: SERVER_TYPES.AREA,
  gate: SERVER_TYPES.GATE,
};

/** What a join token grants. Signed as a whole — no field can be changed without the cluster secret. */
export type JoinTokenClaims = {
  v: 1;
  clusterId: string;
  role: JoinRole;
  /** Glob over the joiner's server id: `*` any run, `?` one character. */
  idPattern: string;
  /** Hosts only: the area types the joiner may advertise and serve. Absent = no area types. */
  areaTypes?: string[];
  /** Epoch ms after which the token is refused. */
  expiresAt: number;
  /** Epoch ms the token was minted. */
  issuedAt: number;
  /** Uniqueness only — two tokens minted the same millisecond still differ. */
  nonce: string;
};

export type MintJoinTokenInput = {
  clusterId: string;
  role: JoinRole;
  idPattern: string;
  areaTypes?: string[];
  /** Lifetime in ms from now. */
  ttlMs: number;
  now?: number;
};

export class JoinTokenError extends Error {
  constructor(readonly reason: 'malformed' | 'signature' | 'expired' | 'cluster' | 'role' | 'id' | 'areaTypes', message: string) {
    super(message);
    this.name = 'JoinTokenError';
  }
}

const PREFIX = 'gjt1';
const ROLES: JoinRole[] = ['host', 'connector', 'area', 'gate'];
/** A cluster secret shorter than this is refused — it is the root of every token. */
export const MIN_CLUSTER_SECRET_BYTES = 32;

function secretBytes(secret: string): Buffer {
  const bytes = Buffer.from(secret, 'base64url');
  if (bytes.length < MIN_CLUSTER_SECRET_BYTES) {
    throw new Error(`Cluster secret must be at least ${MIN_CLUSTER_SECRET_BYTES} random bytes (base64url); got ${bytes.length}.`);
  }
  return bytes;
}

function sign(secret: string, body: string): Buffer {
  return createHmac('sha256', secretBytes(secret)).update(`${PREFIX}.${body}`).digest();
}

/** New random cluster secret (base64url, 32 bytes). */
export function generateClusterSecret(): string {
  return randomBytes(32).toString('base64url');
}

export function mintJoinToken(secret: string, input: MintJoinTokenInput): string {
  if (!ROLES.includes(input.role)) throw new Error(`Unknown join role '${input.role}' (expected one of ${ROLES.join(', ')}).`);
  if (!input.clusterId) throw new Error(`A join token needs a clusterId.`);
  if (!input.idPattern) throw new Error(`A join token needs an idPattern.`);
  if (!(input.ttlMs > 0)) throw new Error(`A join token needs a positive ttlMs.`);
  const now = input.now ?? Date.now();
  const claims: JoinTokenClaims = {
    v: 1,
    clusterId: input.clusterId,
    role: input.role,
    idPattern: input.idPattern,
    ...(input.areaTypes ? { areaTypes: [...input.areaTypes] } : {}),
    expiresAt: now + input.ttlMs,
    issuedAt: now,
    nonce: randomBytes(9).toString('base64url'),
  };
  const body = Buffer.from(JSON.stringify(claims)).toString('base64url');
  return `${PREFIX}.${body}.${sign(secret, body).toString('base64url')}`;
}

/** Anchored glob → RegExp (`*` any run, `?` one char, everything else literal). */
export function idPatternMatches(pattern: string, id: string | number): boolean {
  const re = new RegExp('^' + pattern.split('').map(ch => ch === '*' ? '.*' : ch === '?' ? '.' : ch.replace(/[.+^${}()|[\]\\]/g, '\\$&')).join('') + '$');
  return re.test(String(id));
}

export type VerifyJoinTokenExpect = {
  clusterId: string;
  role: JoinRole;
  serverId: string | number;
  now?: number;
};

/**
 * Verify signature first (constant time), then every claim against what the
 * joiner is asking to be. Throws `JoinTokenError` naming the failed check.
 */
export function verifyJoinToken(secret: string, token: unknown, expect: VerifyJoinTokenExpect): JoinTokenClaims {
  if (typeof token !== 'string') throw new JoinTokenError('malformed', 'Join token missing.');
  const parts = token.split('.');
  if (parts.length !== 3 || parts[0] !== PREFIX) throw new JoinTokenError('malformed', 'Join token is not a gjt1 token.');
  const [, body, sig] = parts;
  const given = Buffer.from(sig, 'base64url');
  const wanted = sign(secret, body);
  if (given.length !== wanted.length || !timingSafeEqual(given, wanted)) {
    throw new JoinTokenError('signature', 'Join token signature is invalid (wrong cluster secret or tampered token).');
  }
  let claims: JoinTokenClaims;
  try {
    claims = JSON.parse(Buffer.from(body, 'base64url').toString('utf8'));
  } catch {
    throw new JoinTokenError('malformed', 'Join token body is not JSON.');
  }
  if (claims?.v !== 1 || typeof claims.expiresAt !== 'number' || typeof claims.idPattern !== 'string') {
    throw new JoinTokenError('malformed', 'Join token claims are malformed.');
  }
  const now = expect.now ?? Date.now();
  if (now >= claims.expiresAt) {
    throw new JoinTokenError('expired', `Join token expired at ${new Date(claims.expiresAt).toISOString()}.`);
  }
  if (claims.clusterId !== expect.clusterId) {
    throw new JoinTokenError('cluster', `Join token is for cluster '${claims.clusterId}', not '${expect.clusterId}'.`);
  }
  if (claims.role !== expect.role) {
    throw new JoinTokenError('role', `Join token grants role '${claims.role}', not '${expect.role}'.`);
  }
  if (!idPatternMatches(claims.idPattern, expect.serverId)) {
    throw new JoinTokenError('id', `Server id '${expect.serverId}' does not match the token's idPattern '${claims.idPattern}'.`);
  }
  return claims;
}
